import { computed, ref, watch, onBeforeUnmount, useHost } from "vue";
import type { ComputedRef, Ref } from "vue";
import type { DrawerProps, DrawerSide } from "./drawer.types";
import type { SplitterDirection } from "../Splitter/splitter.types";
import { drawerBaseClass, drawerSideClasses, drawerResizableHandleClasses } from "./drawer.styles";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import { useSharedState } from "composables/useSharedState";
import { createEventDispatcher } from "@/runtime/shared/events";
import { parseBoolAttr } from "@/runtime/shared/attrs";

const OVERLAY_IN: Keyframe[] = [{ opacity: 0 }, { opacity: 1 }];
const OVERLAY_OUT: Keyframe[] = [{ opacity: 1 }, { opacity: 0 }];
const ANIM_OPTIONS: KeyframeAnimationOptions = { duration: 250, easing: "ease-out", fill: "forwards" };

const getKeyframes = (side: DrawerSide): { in: Keyframe[]; out: Keyframe[] } => {
  const transforms: Record<DrawerSide, { hidden: string; visible: string }> = {
    top: { hidden: "translateY(-100%)", visible: "translateY(0)" },
    right: { hidden: "translateX(100%)", visible: "translateX(0)" },
    bottom: { hidden: "translateY(100%)", visible: "translateY(0)" },
    left: { hidden: "translateX(-100%)", visible: "translateX(0)" },
  };
  const { hidden, visible } = transforms[side];
  return {
    in: [{ transform: hidden }, { transform: visible }],
    out: [{ transform: visible }, { transform: hidden }],
  };
};

interface AnimCtx {
  active: Animation[];
  cancel: () => void;
}

/** e-primitive uses display:contents on host; animations need the inner element. */
const animTarget = (el: HTMLElement | null | undefined): HTMLElement | null => {
  if (!el) return null;
  if (el.tagName.toLowerCase() === "e-primitive") {
    return (el.firstElementChild as HTMLElement) ?? el;
  }
  return el;
};

const makeAnimCtx = (): AnimCtx => {
  const ctx: AnimCtx = {
    active: [],
    cancel: () => {
      for (const a of ctx.active) a.cancel();
      ctx.active = [];
    },
  };
  return ctx;
};

type DrawerDispatcher = ReturnType<typeof createEventDispatcher>;
type DrawerState = ReturnType<typeof useSharedState<DrawerProps>>;

const makeOpen =
  (
    anim: AnimCtx,
    internalOpen: Ref<boolean>,
    isClosing: Ref<boolean>,
    isAnimated: ComputedRef<boolean>,
    isResizable: ComputedRef<boolean>,
    drawerDefaultSize: ComputedRef<number>,
    drawerSize: Ref<number>,
    currentSide: ComputedRef<DrawerSide>,
    contentRef: Ref<HTMLElement | null> | undefined,
    overlayRef: Ref<HTMLElement | null> | undefined,
    dispatchEvent: DrawerDispatcher,
  ) =>
  (): void => {
    if (internalOpen.value) return;
    anim.cancel();
    isClosing.value = false;
    if (isResizable.value) drawerSize.value = drawerDefaultSize.value;
    internalOpen.value = true;
    dispatchEvent("update:open", true);
    dispatchEvent("open");
    requestAnimationFrame(() => {
      const el = animTarget(contentRef?.value);
      const overlay = overlayRef?.value;
      const anims: Animation[] = [];
      if (el && isAnimated.value) anims.push(el.animate(getKeyframes(currentSide.value).in, ANIM_OPTIONS));
      if (overlay && isAnimated.value) anims.push(overlay.animate(OVERLAY_IN, ANIM_OPTIONS));
      if (anims.length > 0) anim.active = anims;
    });
  };

const makeClose =
  (
    anim: AnimCtx,
    internalOpen: Ref<boolean>,
    isClosing: Ref<boolean>,
    isAnimated: ComputedRef<boolean>,
    currentSide: ComputedRef<DrawerSide>,
    contentRef: Ref<HTMLElement | null> | undefined,
    overlayRef: Ref<HTMLElement | null> | undefined,
    host: Element | null | undefined,
    dispatchEvent: DrawerDispatcher,
  ) =>
  (): void => {
    if (!internalOpen.value) return;
    internalOpen.value = false;
    host?.removeAttribute("open");
    dispatchEvent("update:open", false);
    dispatchEvent("close");
    const el = animTarget(contentRef?.value);
    const overlay = overlayRef?.value;
    if (!isAnimated.value) return;
    isClosing.value = true;
    anim.cancel();
    const closingAnims: Animation[] = [];
    if (el) closingAnims.push(el.animate(getKeyframes(currentSide.value).out, ANIM_OPTIONS));
    if (overlay) closingAnims.push(overlay.animate(OVERLAY_OUT, ANIM_OPTIONS));
    if (closingAnims.length === 0) {
      isClosing.value = false;
      return;
    }
    anim.active = closingAnims;
    closingAnims[0].finished
      .then(() => {
        anim.active = [];
        isClosing.value = false;
      })
      .catch(() => {});
  };

const setupClickOutside = (
  isOpen: ComputedRef<boolean>,
  state: DrawerState,
  contentRef: Ref<HTMLElement | null> | undefined,
  close: () => void,
): void => {
  const handle = (event: MouseEvent) => {
    const el = contentRef?.value;
    if (!el || event.composedPath().includes(el)) return;
    close();
  };
  watch(
    () => isOpen.value && parseBoolAttr(state.closeOnClickOutside?.value, true),
    (shouldListen) => {
      if (shouldListen) document.addEventListener("mousedown", handle, true);
      else document.removeEventListener("mousedown", handle, true);
    },
    { immediate: true },
  );
  onBeforeUnmount(() => document.removeEventListener("mousedown", handle, true));
};

const setupEscapeClose = (isOpen: ComputedRef<boolean>, state: DrawerState, close: () => void): void => {
  const handle = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      close();
    }
  };
  watch(
    () => isOpen.value && parseBoolAttr(state.closeOnEscape?.value, true),
    (shouldListen) => {
      if (shouldListen) document.addEventListener("keydown", handle, true);
      else document.removeEventListener("keydown", handle, true);
    },
    { immediate: true },
  );
  onBeforeUnmount(() => document.removeEventListener("keydown", handle, true));
};

type DrawerComputeds = ReturnType<typeof makeDrawerComputeds>;

const makeOpenClosePair = (
  anim: AnimCtx,
  s: DrawerComputeds,
  contentRef: Ref<HTMLElement | null> | undefined,
  overlayRef: Ref<HTMLElement | null> | undefined,
  host: Element | null | undefined,
  dispatchEvent: DrawerDispatcher,
) => {
  const close = makeClose(
    anim,
    s.internalOpen,
    s.isClosing,
    s.isAnimated,
    s.currentSide,
    contentRef,
    overlayRef,
    host,
    dispatchEvent,
  );
  const open = makeOpen(
    anim,
    s.internalOpen,
    s.isClosing,
    s.isAnimated,
    s.isResizable,
    s.drawerDefaultSize,
    s.drawerSize,
    s.currentSide,
    contentRef,
    overlayRef,
    dispatchEvent,
  );
  return { open, close };
};

const makeDragResize =
  (s: DrawerComputeds, close: () => void) =>
  (e: PointerEvent): void => {
    if (!s.isResizable.value) return;
    e.preventDefault();
    const isHorizontal = s.isHorizontalSide.value;
    const dimension = isHorizontal ? window.innerWidth : window.innerHeight;
    const startPos = isHorizontal ? e.clientX : e.clientY;
    const startSize = s.drawerSize.value;
    const side = s.currentSide.value;
    const minSize = s.drawerMinSize.value;
    const maxSize = s.drawerMaxSize.value;
    const prevUserSelect = document.body.style.userSelect;
    const prevCursor = document.body.style.cursor;
    document.body.style.userSelect = "none";
    document.body.style.cursor = isHorizontal ? "col-resize" : "row-resize";
    const iframes = document.querySelectorAll("iframe");
    const prevPointerEvents: string[] = [];
    for (const [i, iframe] of iframes.entries()) {
      prevPointerEvents[i] = iframe.style.pointerEvents;
      iframe.style.pointerEvents = "none";
    }
    const onMove = (moveEvent: PointerEvent): void => {
      const currentPos = isHorizontal ? moveEvent.clientX : moveEvent.clientY;
      const deltaPercent = ((currentPos - startPos) / dimension) * 100;
      const newSize = side === "right" || side === "bottom" ? startSize - deltaPercent : startSize + deltaPercent;
      s.drawerSize.value = Math.max(minSize, Math.min(maxSize, newSize));
      if (newSize < minSize / 2) close();
    };
    const onUp = (): void => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.body.style.userSelect = prevUserSelect;
      document.body.style.cursor = prevCursor;
      for (const [i, iframe] of iframes.entries()) {
        iframe.style.pointerEvents = prevPointerEvents[i];
      }
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  };

export interface UseDrawerOptions {
  props: Readonly<DrawerProps>;
  contentRef?: Ref<HTMLElement | null>;
  overlayRef?: Ref<HTMLElement | null>;
}

export interface UseDrawerReturn {
  classes: ComputedRef<string>;
  visible: ComputedRef<boolean>;
  overlayVisible: ComputedRef<boolean>;
  isOpen: ComputedRef<boolean>;
  open: () => void;
  close: () => void;
  isResizable: ComputedRef<boolean>;
  drawerSizeStyle: ComputedRef<Record<string, string>>;
  resizableHandleClass: ComputedRef<string>;
  drawerHandleDirection: ComputedRef<SplitterDirection>;
  onHandlePointerDown: (e: PointerEvent) => void;
}

const makeDrawerComputeds = (props: Readonly<DrawerProps>, state: DrawerState) => {
  const internalOpen = ref(state.open?.value ?? false);
  const isOpen = computed(() => internalOpen.value);
  const isClosing = ref(false);
  const visible = computed(() => internalOpen.value || isClosing.value);
  const isAnimated = computed(() => parseBoolAttr(state.animated?.value, true));
  const currentSide = computed(() => (state.side?.value ?? "right") as DrawerSide);
  const hasOverlay = computed(() => parseBoolAttr(state.overlay?.value, true));
  const overlayVisible = computed(() => visible.value && hasOverlay.value);
  const isResizable = computed(() => parseBoolAttr(props.resizable, false));
  const drawerDefaultSize = computed(() => Number(props.defaultSize) || 25);
  const drawerMinSize = computed(() => Number(props.minSize) || 10);
  const drawerMaxSize = computed(() => Number(props.maxSize) || 80);
  const drawerSize = ref(drawerDefaultSize.value);
  const isHorizontalSide = computed(() => currentSide.value === "left" || currentSide.value === "right");
  const drawerSizeStyle = computed((): Record<string, string> => {
    if (!isResizable.value) return {};
    return isHorizontalSide.value ? { width: `${drawerSize.value}%` } : { height: `${drawerSize.value}%` };
  });
  const resizableHandleClass = computed(() => drawerResizableHandleClasses[currentSide.value]);
  const drawerHandleDirection = computed((): SplitterDirection => (isHorizontalSide.value ? "horizontal" : "vertical"));
  const classes = computed(() =>
    mergeClassProp(`${drawerBaseClass} ${drawerSideClasses[currentSide.value]}`, props.class),
  );
  return {
    internalOpen,
    isOpen,
    isClosing,
    visible,
    isAnimated,
    currentSide,
    overlayVisible,
    isResizable,
    drawerDefaultSize,
    drawerMinSize,
    drawerMaxSize,
    drawerSize,
    isHorizontalSide,
    drawerSizeStyle,
    resizableHandleClass,
    drawerHandleDirection,
    classes,
  };
};

export const useDrawer = (options: UseDrawerOptions): UseDrawerReturn => {
  const { props } = options;
  const host = useHost();
  const state = useSharedState(props);
  const dispatchEvent = createEventDispatcher(host);
  const s = makeDrawerComputeds(props, state);
  const anim = makeAnimCtx();
  const { open, close } = makeOpenClosePair(anim, s, options.contentRef, options.overlayRef, host, dispatchEvent);
  const onHandlePointerDown = makeDragResize(s, close);
  setupClickOutside(s.isOpen, state, options.contentRef, close);
  setupEscapeClose(s.isOpen, state, close);
  watch(
    () => state.open?.value,
    (v) => {
      if (v && !s.internalOpen.value) open();
      else if (!v && s.internalOpen.value) close();
    },
    { flush: "post" },
  );
  onBeforeUnmount(anim.cancel);
  return {
    classes: s.classes,
    visible: s.visible,
    overlayVisible: s.overlayVisible,
    isOpen: s.isOpen,
    open,
    close,
    isResizable: s.isResizable,
    drawerSizeStyle: s.drawerSizeStyle,
    resizableHandleClass: s.resizableHandleClass,
    drawerHandleDirection: s.drawerHandleDirection,
    onHandlePointerDown,
  };
};
